import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { adminProfitabilityAPI, analyticsAPI, codesAPI, adminSubscribersAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Activity, AlertCircle, AlertTriangle, BarChart3, Check, CheckCircle, ChevronLeft, Copy, Database, DollarSign, Download, Gift, Loader2, MessageSquare, PieChart, RefreshCw, Send, Shield, TrendingDown, TrendingUp, Users, X, Zap
} from 'lucide-react';

// Format currency
const formatCurrency = (value) => {
  if (value === undefined || value === null) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

// Format percentage
const formatPercent = (value) => {
  if (value === undefined || value === null) return '0%';
  return `${value.toFixed(1)}%`;
};

// Get alert level badge styling
const getAlertBadge = (level) => {
  const styles = {
    HEALTHY: { variant: 'default', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
    WARNING: { variant: 'default', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    HIGH_RISK: { variant: 'default', className: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    CRITICAL: { variant: 'default', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
  };
  return styles[level] || styles.HEALTHY;
};

// Plan badge styling
// Display name mapping: PRO→"Investor", PRO_PLUS→"Pro"
const getPlanDisplayName = (planType) => {
  const names = { FREE: 'Free', PRO: 'Investor', PRO_PLUS: 'Pro', FOUNDER: 'Founder' };
  return names[planType] || planType;
};

const getPlanBadge = (planType, isFounder) => {
  if (isFounder) return { className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' };
  
  // Styles for 3 plans + Founder
  const styles = {
    FREE: { className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
    PRO: { className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },      // "Investor"
    PRO_PLUS: { className: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },          // "Pro"
    FOUNDER: { className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  };
  return styles[planType] || styles.FREE;
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendUp }) => (
  <Card className="bg-slate-800/50 border-slate-700">
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wide">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-lg ${trend ? (trendUp ? 'bg-green-500/20' : 'bg-red-500/20') : 'bg-slate-700/50'}`}>
          {trend ? (
            trendUp ? <TrendingUp className="w-5 h-5 text-green-400" /> : <TrendingDown className="w-5 h-5 text-red-400" />
          ) : (
            <Icon className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

// Plan Profitability Card
const PlanCard = ({ plan }) => {
  const isNegativeMargin = plan.estimated_gross_margin < 0;
  
  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base text-white">{plan.plan_type}</CardTitle>
          <Badge className={getPlanBadge(plan.plan_type).className}>
            {formatCurrency(plan.plan_price)}/mo
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-slate-500">Users</p>
            <p className="text-white font-medium">{plan.total_users}</p>
          </div>
          <div>
            <p className="text-slate-500">Analyses</p>
            <p className="text-white font-medium">{plan.total_analyses}</p>
          </div>
          <div>
            <p className="text-slate-500">Cache Hit Rate</p>
            <p className="text-white font-medium">{formatPercent(plan.cache_hit_rate)}</p>
          </div>
          <div>
            <p className="text-slate-500">Avg Cost/Analysis</p>
            <p className="text-white font-medium">{formatCurrency(plan.avg_cost_per_analysis)}</p>
          </div>
        </div>
        
        <div className="pt-2 border-t border-slate-700">
          <div className="flex justify-between items-center">
            <span className="text-slate-400 text-sm">Gross Margin</span>
            <span className={`font-semibold ${isNegativeMargin ? 'text-red-400' : 'text-green-400'}`}>
              {formatCurrency(plan.estimated_gross_margin)} ({formatPercent(plan.gross_margin_pct)})
            </span>
          </div>
          <div className="flex justify-between items-center mt-1">
            <span className="text-slate-500 text-xs" title="Attributed per-request estimate used to compare plans — NOT the RentCast bill">Est. attributed cost: {formatCurrency(plan.total_estimated_cost)}</span>
            <span className="text-slate-500 text-xs">Revenue: {formatCurrency(plan.estimated_plan_revenue)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// User Row Component
const UserRow = ({ user, onClick }) => {
  const alertStyle = getAlertBadge(user.alert_level);
  const planStyle = getPlanBadge(user.plan_type, user.is_founder);
  
  return (
    <tr 
      className="border-b border-slate-700/50 hover:bg-slate-700/30 cursor-pointer transition-colors"
      onClick={() => onClick(user)}
    >
      <td className="py-3 px-4">
        <div>
          <p className="text-white text-sm font-medium">
            {user.display_name || user.email || user.user_id}
          </p>
          {user.email && user.display_name && (
            <p className="text-slate-500 text-xs">{user.email}</p>
          )}
        </div>
      </td>
      <td className="py-3 px-4">
        <Badge className={planStyle.className}>
          {user.is_founder ? 'FOUNDER' : user.plan_type}
        </Badge>
      </td>
      <td className="py-3 px-4 text-slate-300 text-sm">{user.total_analyses}</td>
      <td className="py-3 px-4 text-slate-300 text-sm">{formatPercent(user.cache_hit_rate)}</td>
      <td className="py-3 px-4 text-slate-300 text-sm">{formatCurrency(user.estimated_cost)}</td>
      <td className="py-3 px-4 text-slate-300 text-sm">{formatCurrency(user.cost_per_analysis || (user.estimated_cost / Math.max(user.total_analyses, 1)))}</td>
      <td className="py-3 px-4 text-slate-300 text-sm">{formatPercent(user.cost_to_revenue_ratio * 100)}</td>
      <td className="py-3 px-4">
        <Badge className={alertStyle.className}>
          {user.alert_level}
        </Badge>
      </td>
    </tr>
  );
};

// Alert Summary Component
const AlertSummary = ({ alerts }) => {
  const { CRITICAL = [], HIGH_RISK = [], WARNING = [] } = alerts?.alerts || {};
  const totalFlagged = alerts?.total_flagged || 0;
  
  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-400" />
          Cost Alerts
        </CardTitle>
        <CardDescription>Users crossing cost thresholds</CardDescription>
      </CardHeader>
      <CardContent>
        {totalFlagged === 0 ? (
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle className="w-5 h-5" />
            <span>All users within healthy cost ratios</span>
          </div>
        ) : (
          <div className="space-y-4">
            {CRITICAL.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400" />
                  <span className="text-red-400 font-medium">Critical ({CRITICAL.length})</span>
                  <span className="text-slate-500 text-xs">&gt;80% cost-to-revenue</span>
                </div>
                {CRITICAL.slice(0, 3).map(user => (
                  <div key={user.user_id} className="pl-6 text-sm">
                    <span className="text-white">{user.display_name || user.user_id}</span>
                    <span className="text-slate-500 ml-2">
                      {formatCurrency(user.estimated_cost)} ({formatPercent(user.cost_to_revenue_ratio * 100)} ratio)
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            {HIGH_RISK.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-400" />
                  <span className="text-orange-400 font-medium">High Risk ({HIGH_RISK.length})</span>
                  <span className="text-slate-500 text-xs">60-80% cost-to-revenue</span>
                </div>
                {HIGH_RISK.slice(0, 3).map(user => (
                  <div key={user.user_id} className="pl-6 text-sm">
                    <span className="text-white">{user.display_name || user.user_id}</span>
                    <span className="text-slate-500 ml-2">
                      {formatCurrency(user.estimated_cost)} ({formatPercent(user.cost_to_revenue_ratio * 100)} ratio)
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            {WARNING.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-400" />
                  <span className="text-yellow-400 font-medium">Warning ({WARNING.length})</span>
                  <span className="text-slate-500 text-xs">40-60% cost-to-revenue</span>
                </div>
                {WARNING.slice(0, 3).map(user => (
                  <div key={user.user_id} className="pl-6 text-sm">
                    <span className="text-white">{user.display_name || user.user_id}</span>
                    <span className="text-slate-500 ml-2">
                      {formatCurrency(user.estimated_cost)} ({formatPercent(user.cost_to_revenue_ratio * 100)} ratio)
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Access Codes Manager Component
const AccessCodesManager = () => {
  const [contactsText, setContactsText] = useState('');
  const [analysesPerCode, setAnalysesPerCode] = useState(20);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [existingCodes, setExistingCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  
  // Load existing codes on mount
  useEffect(() => {
    loadExistingCodes();
  }, []);
  
  const loadExistingCodes = async () => {
    setLoadingCodes(true);
    try {
      const response = await codesAPI.adminList();
      setExistingCodes(response.data.codes || []);
    } catch (err) {
      console.error('Failed to load codes:', err);
    } finally {
      setLoadingCodes(false);
    }
  };
  
  const parseContacts = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const contacts = [];
    const errors = [];
    
    lines.forEach((line, index) => {
      const parts = line.split(',').map(p => p.trim());
      
      if (parts.length < 3) {
        errors.push({ line: index + 1, text: line, error: 'Missing fields (need: First, Last, Email)' });
        return;
      }
      
      const [first_name, last_name, email, phone] = parts;
      
      if (!email || !email.includes('@')) {
        errors.push({ line: index + 1, text: line, error: 'Invalid email' });
        return;
      }
      
      contacts.push({
        first_name: first_name || 'Friend',
        last_name: last_name || '',
        email: email.toLowerCase(),
        phone: phone ? phone.replace(/[\s\-\+\(\)]/g, '') : null
      });
    });
    
    return { contacts, errors };
  };
  
  const handleGenerate = async (sendEmail) => {
    const { contacts, errors } = parseContacts(contactsText);
    
    if (errors.length > 0) {
      toast.error(`${errors.length} line(s) have errors. Check format: First, Last, Email, Phone(optional)`);
      return;
    }
    
    if (contacts.length === 0) {
      toast.error('No valid contacts found');
      return;
    }
    
    setLoading(true);
    setResults(null);
    
    try {
      const response = await codesAPI.adminGenerateAndSend({
        contacts,
        analyses_granted: analysesPerCode,
        send_email: sendEmail,
        features_unlocked: true
      });
      
      setResults(response.data);
      loadExistingCodes();
      
      const { summary } = response.data;
      if (sendEmail) {
        toast.success(`Generated ${summary.codes_generated} codes, sent ${summary.emails_sent} emails`);
      } else {
        toast.success(`Generated ${summary.codes_generated} codes`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate codes');
    } finally {
      setLoading(false);
    }
  };
  
  const buildWhatsAppUrl = (contact, code) => {
    if (!contact.phone) return null;
    const message = encodeURIComponent(
      `Hi ${contact.first_name}! Here's your complimentary CHEKSTE access code: ${code} — ${analysesPerCode} free analyses. Redeem at chekste.com (create account, Billing, Redeem Code). | ¡Hola ${contact.first_name}! Aquí está tu código de cortesía de CHEKSTE: ${code} — ${analysesPerCode} análisis gratis. Canjéalo en chekste.com (crea tu cuenta, Billing, Redeem Code).`
    );
    return `https://wa.me/${contact.phone}?text=${message}`;
  };
  
  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied!');
  };
  
  const copyAllResults = () => {
    if (!results?.results) return;
    const text = results.results
      .filter(r => r.code)
      .map(r => `${r.name}: ${r.code}`)
      .join('\n');
    navigator.clipboard.writeText(text);
    toast.success('All codes copied!');
  };
  
  return (
    <div className="space-y-6">
      {/* Generate Section */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Gift className="w-5 h-5 text-purple-400" />
            Generate & Send Access Codes
          </CardTitle>
          <CardDescription>
            Paste contacts (one per line): First Name, Last Name, Email, Phone (optional)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <textarea
            value={contactsText}
            onChange={(e) => setContactsText(e.target.value)}
            placeholder={`Juan, Perez, juan@email.com, +15551234567\nMaria, Lopez, maria@email.com`}
            className="w-full h-40 px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white font-mono text-sm placeholder:text-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            data-testid="contacts-textarea"
          />
          
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-slate-400 text-sm">Analyses per code:</label>
              <input
                type="number"
                value={analysesPerCode}
                onChange={(e) => setAnalysesPerCode(parseInt(e.target.value) || 20)}
                min={1}
                max={100}
                className="w-20 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-center"
              />
            </div>
            
            <div className="flex gap-2 ml-auto">
              <Button
                onClick={() => handleGenerate(true)}
                disabled={loading || !contactsText.trim()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Generate & Send Email
              </Button>
              <Button
                onClick={() => handleGenerate(false)}
                disabled={loading || !contactsText.trim()}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Gift className="w-4 h-4 mr-2" />}
                Generate Only
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Results Section */}
      {results && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Generation Results
              </CardTitle>
              <Button
                onClick={copyAllResults}
                variant="outline"
                size="sm"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy All
              </Button>
            </div>
            <CardDescription>
              {results.summary.codes_generated} codes generated
              {results.summary.emails_sent > 0 && `, ${results.summary.emails_sent} emails sent`}
              {results.summary.emails_failed > 0 && `, ${results.summary.emails_failed} failed`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2 px-3 text-slate-400 font-medium">Name</th>
                    <th className="text-left py-2 px-3 text-slate-400 font-medium">Email</th>
                    <th className="text-left py-2 px-3 text-slate-400 font-medium">Code</th>
                    <th className="text-center py-2 px-3 text-slate-400 font-medium">Email</th>
                    <th className="text-center py-2 px-3 text-slate-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {results.results.map((r, i) => (
                    <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-2 px-3 text-white">{r.name}</td>
                      <td className="py-2 px-3 text-slate-300 text-xs">{r.email}</td>
                      <td className="py-2 px-3">
                        {r.code ? (
                          <span className="font-mono text-purple-400">{r.code}</span>
                        ) : (
                          <span className="text-red-400 text-xs">{r.email_error || 'Failed'}</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {r.email_sent ? (
                          <Check className="w-4 h-4 text-green-400 inline" />
                        ) : r.email_error ? (
                          <span title={r.email_error}><X className="w-4 h-4 text-red-400 inline" /></span>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {r.code && (
                            <button
                              onClick={() => copyCode(r.code)}
                              className="p-1 hover:bg-slate-600 rounded"
                              title="Copy code"
                            >
                              <Copy className="w-4 h-4 text-slate-400" />
                            </button>
                          )}
                          {r.phone && r.code && (
                            <a
                              href={buildWhatsAppUrl(r, r.code)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 hover:bg-green-600/20 rounded"
                              title="Send via WhatsApp"
                            >
                              <MessageSquare className="w-4 h-4 text-green-400" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Existing Codes Section */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-400" />
              All Access Codes
            </CardTitle>
            <Button
              onClick={loadExistingCodes}
              disabled={loadingCodes}
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingCodes ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          <CardDescription>
            {existingCodes.length} total codes ({existingCodes.filter(c => c.used).length} used, {existingCodes.filter(c => !c.used).length} unused)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingCodes ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : existingCodes.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No codes generated yet</p>
          ) : (
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-800">
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-2 px-3 text-slate-400 font-medium">Code</th>
                    <th className="text-left py-2 px-3 text-slate-400 font-medium">Note</th>
                    <th className="text-center py-2 px-3 text-slate-400 font-medium">Analyses</th>
                    <th className="text-center py-2 px-3 text-slate-400 font-medium">Status</th>
                    <th className="text-left py-2 px-3 text-slate-400 font-medium">Used By</th>
                    <th className="text-left py-2 px-3 text-slate-400 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {existingCodes.map((code, i) => (
                    <tr key={i} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-2 px-3">
                        <span className="font-mono text-purple-400">{code.code}</span>
                      </td>
                      <td className="py-2 px-3 text-slate-300 text-xs max-w-[150px] truncate">
                        {code.note || '—'}
                      </td>
                      <td className="py-2 px-3 text-center text-white">
                        {code.analyses_granted}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {code.used ? (
                          <Badge className="bg-slate-600 text-slate-300">Used</Badge>
                        ) : (
                          <Badge className="bg-green-600/20 text-green-400">Available</Badge>
                        )}
                      </td>
                      <td className="py-2 px-3 text-slate-400 text-xs">
                        {code.used_by ? code.used_by.substring(0, 12) + '...' : '—'}
                      </td>
                      <td className="py-2 px-3 text-slate-500 text-xs">
                        {code.created_at ? new Date(code.created_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Main Admin Dashboard
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Declared HERE, with the other hooks, and not beside the handler further down:
  // everything below `if (loading) return ...` runs conditionally, so a useState
  // there changes the hook order between renders. react-hooks/rules-of-hooks is an
  // ERROR, not a warning, so it also broke the production build.
  const [exportingAnalyses, setExportingAnalyses] = useState(false);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [users, setUsers] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [heavyUsers, setHeavyUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [conversionStats, setConversionStats] = useState(null);
  // Subscriber registry (profiles-based — every user, real MRR)
  const [subscribers, setSubscribers] = useState(null);
  const [subSearch, setSubSearch] = useState('');
  const [subSourceFilter, setSubSourceFilter] = useState('');  // '', paid, comp, founder, free
  const [subToCancel, setSubToCancel] = useState(null);  // subscriber row pending founder-cancel
  const [subCanceling, setSubCanceling] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      
      // First verify admin access
      const accessCheck = await adminProfitabilityAPI.verifyAccess();
      if (!accessCheck.data?.is_admin) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }
      
      const [summaryRes, plansRes, usersRes, alertsRes, heavyRes, subsRes] = await Promise.all([
        adminProfitabilityAPI.getSummary(month),
        adminProfitabilityAPI.getPlanProfitability(month),
        adminProfitabilityAPI.getUsers(month, 0, 100),
        adminProfitabilityAPI.getAlerts(month),
        adminProfitabilityAPI.getHeavyUsers(month),
        adminSubscribersAPI.list(),  // every user (profiles-based), real MRR
      ]);

      setSummary(summaryRes.data);
      setPlanData(plansRes.data);
      setUsers(usersRes.data?.users || []);
      setAlerts(alertsRes.data);
      setHeavyUsers(heavyRes.data?.heavy_users || []);
      setSubscribers(subsRes.data || null);
      
      // Fetch conversion stats (non-blocking)
      try {
        const convRes = await analyticsAPI.getConversionStats();
        setConversionStats(convRes.data);
      } catch (convErr) {
        console.warn('Failed to fetch conversion stats:', convErr);
      }
      
    } catch (err) {
      console.error('Failed to fetch profitability data:', err);
      if (err.response?.status === 401) {
        // Not logged in - redirect to login
        navigate('/');
        return;
      }
      if (err.response?.status === 403) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }
      setError(err.response?.data?.detail || 'Failed to load profitability data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [month, navigate]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdminCancel = async () => {
    if (!subToCancel) return;
    setSubCanceling(true);
    try {
      const res = await adminSubscribersAPI.cancel(subToCancel.user_id);
      const endStr = res?.data?.end_date_str || res?.data?.current_period_end;
      toast.success(`Canceled ${subToCancel.email}${endStr ? ` — access until ${endStr}` : ''}`);
      setSubToCancel(null);
      await fetchData();  // refresh so the row shows "Canceling"
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setSubCanceling(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
  };
  
  const handleRecalculate = async () => {
    if (!window.confirm('This will recalculate profitability for all users. Continue?')) return;
    
    setRefreshing(true);
    try {
      await adminProfitabilityAPI.recalculateAll(month);
      await fetchData();
    } catch (err) {
      console.error('Failed to recalculate:', err);
      setError('Failed to recalculate profitability');
    } finally {
      setRefreshing(false);
    }
  };
  
  const handleUserClick = async (user) => {
    try {
      const res = await adminProfitabilityAPI.getUserDetail(user.user_id, month);
      setSelectedUser(res.data);
    } catch (err) {
      console.error('Failed to fetch user detail:', err);
    }
  };
  
  // Access Denied screen
  if (accessDenied) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-500/20 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Shield className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Admin Access Required</h1>
          <p className="text-slate-400 mb-6">
            This page is restricted to administrators only. If you believe you should have access, 
            please contact admin@digiscon.com.
          </p>
          <Button onClick={() => navigate('/')} className="bg-emerald-600 hover:bg-emerald-700">
            Return to Home
          </Button>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading profitability data...</p>
        </div>
      </div>
    );
  }
  
  // Download the analysis corpus as a spreadsheet.
  //
  // Goes through the shared `api` client so the bearer token is attached the same way
  // every other admin call attaches it — the session lives in localStorage, so a plain
  // link to the endpoint would arrive unauthenticated and 401. Downloading in JS is what
  // lets an authenticated GET become a file.
  const handleExportAnalyses = async () => {
    setExportingAnalyses(true);
    try {
      const res = await api.get('/api/admin/analyses/export', {
        params: { limit: 5000, format: 'csv' },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `CHEKSTE_analyses_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Analyses exported');
    } catch (e) {
      toast.error('Could not export analyses');
    } finally {
      setExportingAnalyses(false);
    }
  };

  const platform = summary?.platform || {};
  const plans = planData?.plans || {};
  
  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-4 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-slate-400 hover:text-white min-h-[40px] touch-manipulation"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-400" />
                  Financial Protection Dashboard
                </h1>
                <p className="text-sm text-slate-400">Monitor API costs and platform profitability</p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white rounded-md px-3 py-2 text-sm min-h-[40px] flex-1 sm:flex-none min-w-0"
              >
                {/* Generate last 6 months */}
                {Array.from({ length: 6 }).map((_, i) => {
                  const d = new Date();
                  d.setMonth(d.getMonth() - i);
                  const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                  const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                  return <option key={val} value={val}>{label}</option>;
                })}
              </select>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportAnalyses}
                disabled={exportingAnalyses}
                className="border-slate-600 text-slate-200 hover:text-white min-h-[40px]"
                data-testid="export-analyses-csv"
                title="Address, area, ZIP, type and resulting ARV for every analysis run"
              >
                <Download className="w-4 h-4 mr-1" />
                {exportingAnalyses ? 'Exporting...' : 'Analyses CSV'}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                className="border-slate-600 text-slate-300 hover:text-white min-h-[40px] touch-manipulation"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleRecalculate}
                disabled={refreshing}
                className="border-slate-600 text-slate-300 hover:text-white min-h-[40px] touch-manipulation"
              >
                <Database className="w-4 h-4 mr-2" />
                Recalculate All
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {error && (
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 text-red-400">
            {error}
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* QUOTA BANNER — the same ladder the alert emails use, so the panel and the
            inbox never disagree. The email is what makes anyone look; this is what they
            act on once they are here. The panel alone is what failed last period: the
            numbers were already on this page when the account went 35 requests over. */}
        {summary?.rentcast_quota && (() => {
          const q = summary.rentcast_quota;
          // Config not confirmed => say so instead of showing a percentage of a quota we
          // are not sure of. A green figure derived from the developer defaults (50
          // included) against a Foundation account would be worse than no figure.
          if (!q.config_confirmed) {
            return (
              <div className="mb-6 rounded-lg border border-amber-500/40 bg-amber-900/25 p-4"
                   data-testid="quota-banner-unconfirmed">
                <p className="font-semibold text-amber-300">RentCast quota alerts are OFF</p>
                <p className="text-sm text-amber-200/80 mt-1">
                  The plan configuration could not be confirmed{q.config_problem ? `: ${q.config_problem}` : '.'}
                  {' '}Calls are still being counted ({q.calls?.toLocaleString()} this period), but no
                  threshold email will be sent until this is fixed.
                </p>
              </div>
            );
          }
          const pct = q.percent ?? 0;
          const tono = q.breaker_tripped ? 'red' : pct >= 100 ? 'red' : pct >= 95 ? 'red' : pct >= 80 ? 'amber' : 'slate';
          if (tono === 'slate' && q.projected <= q.included) return null;
          const clases = {
            red: 'border-red-500/40 bg-red-900/25 text-red-300',
            amber: 'border-amber-500/40 bg-amber-900/25 text-amber-300',
            slate: 'border-slate-600 bg-slate-800/50 text-slate-300',
          }[tono];
          return (
            <div className={`mb-6 rounded-lg border p-4 ${clases}`} data-testid="quota-banner">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <p className="font-semibold">
                  {q.breaker_tripped
                    ? 'Runaway breaker OPEN — RentCast calls are blocked'
                    : pct >= 100
                    ? 'RentCast quota exceeded — overage is accruing'
                    : pct >= 80
                    ? `${pct}% of the RentCast quota used`
                    : 'On pace to exceed the RentCast quota'}
                </p>
                <p className="text-sm opacity-90" data-testid="quota-banner-figures">
                  {q.calls?.toLocaleString()} / {q.included?.toLocaleString()} calls
                  {' · '}day {q.elapsed_days} of {q.period_days}
                  {' · '}projected {q.projected?.toLocaleString()}
                </p>
              </div>
              <p className="text-xs opacity-70 mt-1">
                Billing period from {q.period} — our count, not RentCast's invoice.
              </p>
            </div>
          );
        })()}

        {/* Platform Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {/* API Cost Card with Breakdown */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  {/* Labelled "RentCast Bill (actual)" until this read 390 requests while
                      RentCast's own dashboard read 1,035 of 1,000 with an overage charge.
                      It was never RentCast's figure: it is ours, derived from calls we
                      count on our side. It now counts every dispatched call instead of
                      assuming three per analysis, but it still will not reconcile to the
                      cent — RentCast bills under its own rules about failures and
                      retries. Saying whose number this is, and naming the authority, is
                      more honest than a word that claims it came from them. */}
                  <p className="text-xs text-slate-400 uppercase tracking-wide">RentCast Cost (our count)</p>
                  <p className="text-2xl font-bold text-white mt-1">{formatCurrency(platform.real_api_bill ?? platform.total_estimated_cost)}</p>
                  <p className="text-[10px] text-slate-500">plan base fee + overage, estimated</p>
                  {summary?.api_cost_breakdown && (
                    <div className="mt-1 space-y-0.5">
                      <p className="text-xs text-slate-500" data-testid="rentcast-our-count">
                        {summary.api_cost_breakdown.total_requests} / {summary.api_cost_breakdown.included_requests} calls counted
                      </p>
                      {summary.api_cost_breakdown.requests_over_quota > 0 && (
                        <p className="text-xs text-amber-400">
                          +{summary.api_cost_breakdown.requests_over_quota} overage @ ${summary.api_cost_breakdown.overage_rate}/req
                        </p>
                      )}
                      <p className="text-[10px] text-slate-500 leading-snug" data-testid="rentcast-authority-note">
                        Our count of calls we dispatched — not RentCast's invoice.
                        {' '}
                        <a
                          href="https://app.rentcast.io/app/api"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-slate-400 hover:text-slate-300"
                        >
                          RentCast's dashboard is the authority
                        </a>
                        {' '}on billing; check it before changing plan.
                      </p>
                    </div>
                  )}
                </div>
                <div className="p-2 rounded-lg bg-slate-700/50">
                  <DollarSign className="w-5 h-5 text-slate-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <StatCard
            title="Revenue (MRR)"
            value={formatCurrency(platform.total_estimated_revenue)}
            subtitle="Active subscriptions"
            icon={TrendingUp}
          />
          <StatCard
            title="Gross Margin"
            value={formatCurrency(platform.estimated_gross_margin)}
            subtitle={formatPercent(platform.gross_margin_pct)}
            trend={true}
            trendUp={platform.estimated_gross_margin >= 0}
          />
          <StatCard
            title="Registered Users"
            value={platform.registered_users ?? platform.total_users ?? users.length}
            subtitle={`${heavyUsers.length} heavy users`}
            icon={Users}
          />
          <StatCard
            title="Hard Stops"
            value={summary?.hard_stop_stats?.blocked_users_count || 0}
            subtitle={`${summary?.hard_stop_stats?.blocked_requests_count || 0} blocked requests`}
            icon={AlertCircle}
          />
          
          {/* Plan Recommendation Card */}
          {summary?.plan_recommendation && (
            <Card className={`border ${
              summary.plan_recommendation.should_upgrade 
                ? 'bg-amber-900/30 border-amber-600/50' 
                : 'bg-emerald-900/30 border-emerald-600/50'
            }`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Plan Recommendation</p>
                    {summary.plan_recommendation.should_upgrade ? (
                      <>
                        <p className="text-lg font-bold text-amber-400 mt-1">
                          Upgrade to {summary.plan_recommendation.cheapest_plan.charAt(0).toUpperCase() + summary.plan_recommendation.cheapest_plan.slice(1)}
                        </p>
                        {summary.plan_recommendation.potential_monthly_savings > 0 && (
                          <p className="text-sm text-amber-300">
                            Save ${summary.plan_recommendation.potential_monthly_savings.toFixed(2)}/mo
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-lg font-bold text-emerald-400 mt-1">
                        Optimal Plan ✓
                      </p>
                    )}
                    <p className="text-sm text-slate-300 mt-1">
                      {summary.plan_recommendation.recommendation}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Projected: {summary.plan_recommendation.projected_monthly_requests} req/mo
                    </p>
                    {/* This card said "Optimal Plan ✓" while the account was 35 requests
                        over its quota and accruing an overage charge. It was not wrong
                        about the arithmetic — it was fed a count that assumed three calls
                        per analysis. It is still only as good as the count it reads, and
                        that count is ours, so it says so. */}
                    <p className="text-[10px] text-slate-500 mt-1 leading-snug" data-testid="plan-recommendation-basis">
                      Based on our own call count, not RentCast's billed usage.
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg ${
                    summary.plan_recommendation.should_upgrade ? 'bg-amber-500/20' : 'bg-emerald-500/20'
                  }`}>
                    <TrendingUp className={`w-5 h-5 ${
                      summary.plan_recommendation.should_upgrade ? 'text-amber-400' : 'text-emerald-400'
                    }`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-slate-800 border-slate-700 w-full max-w-full overflow-x-auto flex-nowrap justify-start">
            <TabsTrigger value="overview" className="data-[state=active]:bg-slate-700">
              <PieChart className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="plans" className="data-[state=active]:bg-slate-700">
              <BarChart3 className="w-4 h-4 mr-2" />
              Plans
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-slate-700">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="subscribers" className="data-[state=active]:bg-slate-700">
              <DollarSign className="w-4 h-4 mr-2" />
              Subscribers
            </TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-slate-700">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Alerts
            </TabsTrigger>
            <TabsTrigger value="access-codes" className="data-[state=active]:bg-slate-700">
              <Gift className="w-4 h-4 mr-2" />
              Access Codes
            </TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AlertSummary alerts={alerts} />
              
              {/* Conversion Stats Card */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Zap className="w-5 h-5 text-purple-400" />
                    Conversion Funnel
                  </CardTitle>
                  <CardDescription>User conversion tracking</CardDescription>
                </CardHeader>
                <CardContent>
                  {conversionStats ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-slate-700/50 rounded-lg p-3">
                          <p className="text-2xl font-bold text-white">{conversionStats.limit_reached || 0}</p>
                          <p className="text-xs text-slate-400">Limit Reached</p>
                        </div>
                        <div className="bg-slate-700/50 rounded-lg p-3">
                          <p className="text-2xl font-bold text-white">{conversionStats.upgrade_click || 0}</p>
                          <p className="text-xs text-slate-400">Upgrade Clicks</p>
                        </div>
                        <div className="bg-slate-700/50 rounded-lg p-3">
                          <p className="text-2xl font-bold text-white">{conversionStats.upgrade_prompt_shown || 0}</p>
                          <p className="text-xs text-slate-400">Prompts Shown</p>
                        </div>
                        <div className="bg-slate-700/50 rounded-lg p-3">
                          <p className="text-2xl font-bold text-white">{conversionStats.trial_started || 0}</p>
                          <p className="text-xs text-slate-400">Trials Started</p>
                        </div>
                      </div>
                      {conversionStats.limit_to_upgrade_rate !== undefined && (
                        <div className="pt-3 border-t border-slate-700">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Limit → Upgrade Rate</span>
                            <span className="text-purple-400 font-medium">{conversionStats.limit_to_upgrade_rate}%</span>
                          </div>
                          {conversionStats.limit_to_trial_rate !== undefined && (
                            <div className="flex justify-between text-sm mt-1">
                              <span className="text-slate-400">Limit → Trial Rate</span>
                              <span className="text-blue-400 font-medium">{conversionStats.limit_to_trial_rate}%</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-slate-500">
                      <p>No conversion data yet</p>
                      <p className="text-xs mt-1">Data will appear as users interact with upgrade prompts</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Activity className="w-5 h-5 text-emerald-400" />
                    Top Expensive Users
                  </CardTitle>
                  <CardDescription>Highest API cost this month</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {users.slice(0, 5).map((user, idx) => (
                      <div key={user.user_id} className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0">
                        <div className="flex items-center gap-3">
                          <span className="text-slate-500 text-sm w-6">#{idx + 1}</span>
                          <div>
                            <p className="text-white text-sm">{user.display_name || user.email || user.user_id}</p>
                            <div className="flex items-center gap-2">
                              <Badge className={getPlanBadge(user.plan_type, user.is_founder).className + ' text-xs'}>
                                {user.is_founder ? 'FOUNDER' : user.plan_type}
                              </Badge>
                              <span className="text-slate-500 text-xs">{user.total_analyses} analyses</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-medium">{formatCurrency(user.estimated_cost)}</p>
                          <Badge className={getAlertBadge(user.alert_level).className + ' text-xs'}>
                            {user.alert_level}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Heavy Users */}
            {heavyUsers.length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-orange-400" />
                    Flagged Heavy Users ({heavyUsers.length})
                  </CardTitle>
                  <CardDescription>Users with unusual usage patterns</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-slate-400 text-xs uppercase border-b border-slate-700">
                          <th className="py-2 px-4">User</th>
                          <th className="py-2 px-4">Flags</th>
                          <th className="py-2 px-4">Cache Miss Rate</th>
                          <th className="py-2 px-4">Cost/Analysis</th>
                          <th className="py-2 px-4">Total Cost</th>
                          <th className="py-2 px-4">Alert</th>
                        </tr>
                      </thead>
                      <tbody>
                        {heavyUsers.map(user => (
                          <tr key={user.user_id} className="border-b border-slate-700/50">
                            <td className="py-3 px-4">
                              <p className="text-white text-sm">{user.display_name || user.user_id}</p>
                              <Badge className={getPlanBadge(user.plan_type).className + ' text-xs'}>
                                {user.plan_type}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex flex-wrap gap-1">
                                {user.flags?.map(flag => (
                                  <Badge key={flag} variant="outline" className="text-xs border-orange-500/30 text-orange-400">
                                    {flag.replace(/_/g, ' ')}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-slate-300 text-sm">{formatPercent(user.cache_miss_rate)}</td>
                            <td className="py-3 px-4 text-slate-300 text-sm">{formatCurrency(user.cost_per_analysis)}</td>
                            <td className="py-3 px-4 text-slate-300 text-sm">{formatCurrency(user.estimated_cost)}</td>
                            <td className="py-3 px-4">
                              <Badge className={getAlertBadge(user.alert_level).className}>
                                {user.alert_level}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Plans Tab */}
          <TabsContent value="plans">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.values(plans).map(plan => (
                <PlanCard key={plan.plan_type} plan={plan} />
              ))}
            </div>
          </TabsContent>
          
          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">All Users</CardTitle>
                <CardDescription>Click a user to view detailed metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-slate-400 text-xs uppercase border-b border-slate-700">
                        <th className="py-2 px-4">User</th>
                        <th className="py-2 px-4">Plan</th>
                        <th className="py-2 px-4">Analyses</th>
                        <th className="py-2 px-4">Cache Hit %</th>
                        <th className="py-2 px-4">Cost</th>
                        <th className="py-2 px-4">Cost/Analysis</th>
                        <th className="py-2 px-4">Cost Ratio</th>
                        <th className="py-2 px-4">Alert</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <UserRow key={user.user_id} user={user} onClick={handleUserClick} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            
            {/* User Detail Modal */}
            {selectedUser && (
              <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setSelectedUser(null)}>
                <Card className="bg-slate-800 border-slate-700 max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-white">
                          {selectedUser.profile?.display_name || selectedUser.profitability?.user_id}
                        </CardTitle>
                        <CardDescription>{selectedUser.profile?.email}</CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)} className="text-slate-400">
                        Close
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Subscription Info */}
                    <div className="p-4 bg-slate-700/50 rounded-lg">
                      <h4 className="text-sm font-medium text-slate-400 mb-2">Subscription</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-slate-500 text-xs">Plan</p>
                          <Badge className={getPlanBadge(selectedUser.subscription?.plan_type, selectedUser.subscription?.founder_flag).className}>
                            {selectedUser.subscription?.founder_flag ? 'FOUNDER' : selectedUser.subscription?.plan_type}
                          </Badge>
                        </div>
                        {selectedUser.subscription?.trial_end_date && (
                          <div>
                            <p className="text-slate-500 text-xs">Trial Ends</p>
                            <p className="text-white text-sm">{new Date(selectedUser.subscription.trial_end_date).toLocaleDateString()}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Profitability Metrics */}
                    <div className="p-4 bg-slate-700/50 rounded-lg">
                      <h4 className="text-sm font-medium text-slate-400 mb-2">Profitability Metrics</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Total Analyses</p>
                          <p className="text-white font-medium">{selectedUser.profitability?.total_analyses}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Cache Hits</p>
                          <p className="text-white font-medium">{selectedUser.profitability?.cache_hits}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Cache Hit Rate</p>
                          <p className="text-white font-medium">{formatPercent(selectedUser.profitability?.cache_hit_rate)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Estimated Cost</p>
                          <p className="text-white font-medium">{formatCurrency(selectedUser.profitability?.estimated_cost)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Cost Per Analysis</p>
                          <p className="text-white font-medium">{formatCurrency(selectedUser.profitability?.cost_per_analysis)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Cost-to-Revenue</p>
                          <p className="text-white font-medium">{formatPercent(selectedUser.profitability?.cost_to_revenue_pct)}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Alert Status */}
                    <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                      <span className="text-slate-400">Alert Level</span>
                      <Badge className={getAlertBadge(selectedUser.profitability?.alert_level).className}>
                        {selectedUser.profitability?.alert_level}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Subscribers Tab — every user (profiles-based), real MRR, never usage-gated */}
          <TabsContent value="subscribers">
            {(() => {
              const sum = subscribers?.summary || {};
              const all = subscribers?.subscribers || [];
              const filtered = all.filter(s =>
                (!subSearch || (s.email || '').toLowerCase().includes(subSearch.toLowerCase())) &&
                (!subSourceFilter || s.source === subSourceFilter)
              );
              const srcBadge = { paid: 'bg-green-600', comp: 'bg-purple-600', founder: 'bg-amber-600', free: 'bg-slate-600' };
              const srcLabel = { paid: 'Paid', comp: 'Comp', founder: 'Founder', free: 'Free' };
              const statusColor = { active: 'text-green-400', canceled: 'text-red-400', past_due: 'text-amber-400', free: 'text-slate-400' };
              const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString() : '—'; } catch { return '—'; } };
              const card = (label, value, sub) => (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4">
                    <p className="text-slate-400 text-xs">{label}</p>
                    <p className="text-white text-2xl font-bold">{value}</p>
                    {sub ? <p className="text-slate-500 text-xs mt-1">{sub}</p> : null}
                  </CardContent>
                </Card>
              );
              return (
                <div className="space-y-6">
                  {/* Summary cards */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {card('Total Users', sum.total_users ?? 0)}
                    {card('Paying', sum.paying ?? 0, `${sum.investor ?? 0} Investor · ${sum.pro ?? 0} Pro`)}
                    {card('MRR', formatCurrency(sum.mrr ?? 0), `${sum.active_subscriptions ?? 0} active`)}
                    {card('ARR', formatCurrency(sum.arr ?? 0))}
                    {card('Founders', sum.founders ?? 0)}
                    {card("Comp'd", sum.comp ?? 0, 'influencer access')}
                  </div>

                  {/* Filters */}
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      value={subSearch}
                      onChange={(e) => setSubSearch(e.target.value)}
                      placeholder="Search email…"
                      className="px-3 py-1.5 text-sm bg-slate-800 border border-slate-700 rounded text-white placeholder-slate-500 focus:outline-none focus:border-slate-500"
                      data-testid="subscribers-search"
                    />
                    {['', 'paid', 'comp', 'founder', 'free'].map(s => (
                      <button
                        key={s || 'all'}
                        onClick={() => setSubSourceFilter(s)}
                        className={`px-3 py-1.5 text-xs rounded transition-colors ${subSourceFilter === s ? 'bg-slate-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                      >
                        {s ? srcLabel[s] : 'All'}
                      </button>
                    ))}
                    <span className="text-slate-500 text-xs ml-auto">{filtered.length} of {all.length} shown</span>
                  </div>

                  {/* Table */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-0 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-700 text-slate-400 text-left">
                            <th className="py-3 px-4">Email</th>
                            <th className="py-3 px-4">Name</th>
                            <th className="py-3 px-4">Plan</th>
                            <th className="py-3 px-4">Status</th>
                            <th className="py-3 px-4">Pay</th>
                            <th className="py-3 px-4">Signed Up</th>
                            <th className="py-3 px-4">Used</th>
                            <th className="py-3 px-4">Source</th>
                            <th className="py-3 px-4">Renews/Ends</th>
                            <th className="py-3 px-4">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.length === 0 ? (
                            <tr><td colSpan={10} className="py-6 px-4 text-slate-500 text-center">No subscribers match.</td></tr>
                          ) : filtered.map((s) => (
                            <tr key={s.user_id} className="border-b border-slate-800 hover:bg-slate-800/40">
                              <td className="py-3 px-4 text-white">{s.email || '—'}</td>
                              <td className="py-3 px-4 text-slate-300">{s.name || '—'}</td>
                              <td className="py-3 px-4 text-slate-300">{s.plan}</td>
                              <td className={`py-3 px-4 font-medium ${statusColor[s.status] || 'text-slate-400'}`}>{s.status}</td>
                              <td className="py-3 px-4 text-slate-400">{s.has_payment ? '✓' : '—'}</td>
                              <td className="py-3 px-4 text-slate-400">{fmtDate(s.signup_date)}</td>
                              <td className="py-3 px-4 text-slate-300">{s.analyses_used}{s.analyses_limit === -1 ? '/∞' : `/${s.analyses_limit}`}</td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-0.5 rounded text-white text-xs ${srcBadge[s.source] || 'bg-slate-600'}`}>{srcLabel[s.source] || s.source}</span>
                              </td>
                              <td className="py-3 px-4 text-slate-400">
                                {s.source === 'paid' ? (s.cancel_at_period_end ? `ends ${fmtDate(s.current_period_end)}` : fmtDate(s.current_period_end)) : '—'}
                              </td>
                              <td className="py-3 px-4">
                                {s.source === 'paid' && s.status === 'active' && !s.cancel_at_period_end ? (
                                  <button
                                    onClick={() => setSubToCancel(s)}
                                    data-testid="admin-cancel-btn"
                                    className="px-2 py-1 text-xs text-red-400 border border-red-500/40 rounded hover:bg-red-500/10"
                                  >Cancel</button>
                                ) : s.cancel_at_period_end ? (
                                  <span className="text-amber-400 text-xs">Canceling</span>
                                ) : (
                                  <span className="text-slate-600">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>

                  {/* Founder cancel confirmation dialog */}
                  {subToCancel && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => !subCanceling && setSubToCancel(null)}>
                      <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-white mb-2">Cancel this subscription?</h3>
                        <p className="text-sm text-slate-300 mb-4">
                          This will cancel <span className="text-white font-medium">{subToCancel.email}</span>'s
                          {' '}<span className="text-white font-medium">{subToCancel.plan}</span> plan at the end of their
                          current billing period{subToCancel.current_period_end ? ` (${fmtDate(subToCancel.current_period_end)})` : ''}.
                          They keep access until then and will be emailed a cancellation confirmation. No refund is issued (per Terms of Service §6).
                        </p>
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setSubToCancel(null)}
                            disabled={subCanceling}
                            className="px-3 py-1.5 text-sm text-slate-300 border border-slate-600 rounded hover:bg-slate-800 disabled:opacity-50"
                          >Keep active</button>
                          <button
                            onClick={() => handleAdminCancel()}
                            disabled={subCanceling}
                            data-testid="admin-cancel-confirm"
                            className="px-3 py-1.5 text-sm text-white bg-red-600 rounded hover:bg-red-500 disabled:opacity-50"
                          >{subCanceling ? 'Canceling…' : 'Cancel subscription'}</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <AlertSummary alerts={alerts} />
          </TabsContent>
          
          {/* Access Codes Tab */}
          <TabsContent value="access-codes">
            <AccessCodesManager />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
